import { z } from "zod";
import { logoSchema } from "./logo";
import { mediaUrlSchema } from "./media";

/**
 * The public projection of a session — ADR 0001.
 *
 * This is the complete set of data that legitimately exists BEFORE a participant has
 * an org context, and it is the only thing the `engage_join` role is granted. Defining
 * it as a schema rather than an ad-hoc object matters: the exception to Rule 1 stays
 * exactly this wide, and a field added here is a deliberate widening of what an
 * unauthenticated join code can see, visible in a diff.
 *
 * Note what is absent: no deck, no slides, no participant list, no custom domain.
 */
export const publicSessionSchema = z
  .object({
    sessionId: z.string().uuid(),
    orgId: z.string(),
    status: z.enum(["lobby", "live"]), // never "ended" — the join role cannot see those
    // Defaulted: a phone may hold a join saved before modes existed, and those were live.
    mode: z.enum(["live", "self_paced"]).default("live"),
    /** Self-paced: when it stops taking answers, if it does by itself. */
    closesAt: z.string().datetime().nullable().default(null),
    settings: z
      .object({
        anonymous: z.boolean(),
        requireName: z.boolean(),
      })
      .strict(),
    branding: z
      .object({
        logoUrl: z.string().url().nullable(),
        /** How to size and back that logo on the phone (contracts/logo.ts); null without one. */
        // Defaulted: a phone may hold a join saved before this field existed.
        logoStyle: logoSchema.omit({ src: true }).nullable().default(null),
        primaryColor: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/, "must be a #rrggbb hex colour")
          .nullable(),
        /**
         * The theme's background picture, shaded as the projector shades it (backdrop.ts), the
         * strength already worked out against `background`. Its address is the API's.
         */
        // Defaulted: a phone may hold a join saved before this field existed.
        backgroundImage: z
          .object({ src: mediaUrlSchema, focusX: z.number().min(0).max(1), focusY: z.number().min(0).max(1), shade: z.number().min(0).max(1) })
          .strict()
          .nullable()
          .default(null),
        /** The deck's stage colour, when it has a theme: the phones stand on the same background as the projector. */
        // Defaulted: a phone may hold a join saved before this field existed.
        background: z
          .string()
          .regex(/^#[0-9a-fA-F]{6}$/, "must be a #rrggbb hex colour")
          .nullable()
          .default(null),
      })
      .strict(),
    defaultLocale: z.string(),
  })
  .strict();

export type PublicSession = z.infer<typeof publicSessionSchema>;

/**
 * Join codes are typed by a human off a screen, often in a hurry and often on a phone.
 * Normalising before lookup means "1 2 3 4 5 6" and "abc-def" match what was issued;
 * the DB lookup is exact, so the leniency has to happen here.
 */
export const joinCodeSchema = z
  .string()
  .trim()
  .transform((s) => s.replace(/[\s-]/g, "").toUpperCase())
  .pipe(z.string().min(4).max(12).regex(/^[A-Z0-9]+$/, "letters and digits only"));

export const joinRequestSchema = z
  .object({
    joinCode: joinCodeSchema,
    // Absent in anonymous mode; required when the session sets requireName.
    displayName: z.string().trim().min(1).max(40).optional(),
  })
  .strict();

export type JoinRequest = z.infer<typeof joinRequestSchema>;
